import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAllDeals } from '../hooks/useAllDeals.ts';
import { generateDeepLinkUrl, Environment } from '../services/DeepLinkService.ts';
import { ImageSlide } from '../services/dealsApiService';
import DealsCarousel from './DealsCarousel.tsx';
import './DealsDisplay.css';

interface DealsDisplayProps {
  menuId: string;
  menuType?: 'medical' | 'recreational';
}

/**
 * Get current environment for deep link generation
 */
const getEnvironment = (): Environment => {
  const env = process.env.REACT_APP_ENV;
  return env === 'qa' ? 'qa' : 'production';
};

const DealsDisplay: React.FC<DealsDisplayProps> = ({ menuId, menuType }) => {
  // Use the menu ID directly as the store ID
  // 857 = Olney Medical, 5440 = Olney Recreational
  const storeId = menuId;

  const { deals, loading, error } = useAllDeals(storeId);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [, setSearchParams] = useSearchParams();

  // Handle click on special deal links - replace all URL params with just specialId
  const handleSpecialLinkClick = (specialId: string) => {
    setSearchParams({ specialId });
  };

  // Feature flag for image deals
  const imageDealsEnabled = process.env.REACT_APP_ENABLE_IMAGE_DEALS === 'true';

  // Reset imageLoaded when deals change
  React.useEffect(() => {
    setImageLoaded(false);
  }, [deals]);

  // Determine if we should show deals
  const hasValidDeals = !loading && !error && deals && deals.length > 0 && imageDealsEnabled;

  // During loading, show skeleton placeholder on mobile (maintains space)
  if (loading) {
    return (
      <div className="deals-display-container image-deal deals-loading-skeleton">
        <div className="deals-skeleton-mobile">
          <div className="deals-skeleton-shimmer"></div>
        </div>
      </div>
    );
  }

  // No deals - render collapsible empty container on mobile to prevent jarring disappearance
  if (!hasValidDeals) {
    return <div className="deals-display-container image-deal deals-empty" />;
  }

  // Convert deals array into carousel slides
  // Support both legacy imageUrls format and new imageSlides format
  const slides: ImageSlide[] = deals.flatMap((deal, dealIndex) => {
    // If deal has imageSlides (new multi-slide format), use them directly
    if (deal.imageSlides && deal.imageSlides.length > 0) {
      return deal.imageSlides.map((slide, slideIndex) => ({
        id: slide.id || `deal-${dealIndex}-slide-${slideIndex}`,
        mobile: slide.mobile || '',
        desktop: slide.desktop || '',
        deepLink: slide.deepLink || null,
        regulatoryLink: slide.regulatoryLink || null,
        order: slide.order ?? slideIndex,
      }));
    }
    // Fallback to legacy imageUrls format (single image per deal)
    return [{
      id: `deal-${dealIndex}`,
      mobile: deal.imageUrls?.mobile || '',
      desktop: deal.imageUrls?.desktop || '',
      deepLink: deal.deepLink || null,
      regulatoryLink: deal.regulatoryLink || null,
      order: dealIndex,
    }];
  }).filter(slide => slide.mobile || slide.desktop);  // Only include slides with at least one valid image

  // No valid slides after filtering - show collapsed empty state
  if (slides.length === 0) {
    return <div className="deals-display-container image-deal deals-empty" />;
  }

  // Handle image load complete
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Render single image with optional link wrapper
  const renderSingleImage = (slide: ImageSlide) => {
    const imageElement = (
      <img
        src={slide.desktop || slide.mobile}
        alt="Special deals and promotions"
        className={`deals-image-content ${imageLoaded ? 'loaded' : ''}`}
        draggable={false}
        onLoad={handleImageLoad}
      />
    );

    // Regulatory links (.gov) take priority and open in new tab
    if (slide.regulatoryLink) {
      return (
        <a
          href={slide.regulatoryLink}
          className="deals-image-link deals-regulatory-link"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open regulatory information in new tab"
        >
          {imageElement}
        </a>
      );
    }

    // Wrap in link if deep link is configured
    if (slide.deepLink) {
      // Special links (Dutchie deals) should update URL params without navigating
      if (slide.deepLink.linkType === 'special' && slide.deepLink.specialId) {
        return (
          <button
            type="button"
            onClick={() => handleSpecialLinkClick(slide.deepLink!.specialId!)}
            className="deals-image-link deals-special-link"
            aria-label="Filter menu by this deal"
          >
            {imageElement}
          </button>
        );
      }

      // Filter and product links navigate to full URL
      const deepLinkConfig = menuType ? { ...slide.deepLink, menuType } : slide.deepLink;
      const deepLinkUrl = generateDeepLinkUrl(deepLinkConfig, getEnvironment());
      return (
        <a href={deepLinkUrl} className="deals-image-link">
          {imageElement}
        </a>
      );
    }

    return imageElement;
  };

  // Render deals: single image for 1 deal, carousel for multiple deals
  const renderDeals = () => {
    if (slides.length === 1) {
      // Single deal = static image
      return renderSingleImage(slides[0]);
    }

    // Multiple deals = carousel
    return <DealsCarousel slides={slides} onSpecialLinkClick={handleSpecialLinkClick} onImageLoad={handleImageLoad} menuType={menuType} />;
  };

  return (
    <div className={`deals-display-container image-deal deals-loaded ${imageLoaded ? 'image-ready' : 'image-loading'}`}>
      <div className="deals-content-box">
        {renderDeals()}
      </div>
    </div>
  );
};

export default DealsDisplay;